import { isEmpty } from "lodash-es";
import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { AnimateChangeInHeight } from "@/components/ui/animate-change-height";
import { Button } from "@/components/ui/button/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  BwChevronLeftLine,
  BwChevronRightLine,
  BwFilterLine,
} from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useScreen } from "@/hooks/use-screen";

import { useFilters } from "../_hooks/use-filters";
import type { FilterField, FilterFieldsConfig } from "../types";

type FilterMenuProps = {
  fields: FilterFieldsConfig;
  align?: "start" | "center" | "end";
  /** Trigger button size — header default `icon-md`; the inline bar uses `icon-xs`. */
  size?: ComponentProps<typeof Button>["size"];
  className?: string;
};

// ── Menu tree (calculation) ──────────────────────────────────────────────────
// The declarative config is a FLAT field list; the menu presents grouped fields
// (`group`) as one parent row that opens a submenu. These node shapes + the pure
// `buildMenuNodes` bridge the two: ungrouped → leaf, grouped → one group node.

type MenuLeaf = { type: "leaf"; field: FilterField };
type MenuGroup = {
  type: "group";
  label: string;
  icon: ReactNode;
  /** Opt-in (any member set `groupSearchable`) — show a search box in the submenu. */
  searchable: boolean;
  children: FilterField[];
};
type MenuNode = MenuLeaf | MenuGroup;

/**
 * PURE: flat available fields → interleaved leaf/group nodes, preserving config
 * order both across and within groups. A group is emitted at the position of its
 * FIRST member (mirroring the old menu where "Date" sat right after "Priority"),
 * and takes that member's `groupIcon` (falling back to its `icon`). Search is
 * opt-in: the submenu gets a search box only when a member set `groupSearchable`.
 */
function buildMenuNodes(available: readonly FilterField[]): MenuNode[] {
  const nodes: MenuNode[] = [];
  const byLabel = new Map<string, MenuGroup>();
  for (const field of available) {
    if (!field.group) {
      nodes.push({ type: "leaf", field });
      continue;
    }
    let group = byLabel.get(field.group);
    if (!group) {
      group = {
        type: "group",
        label: field.group,
        icon: field.groupIcon ?? field.icon,
        searchable: false,
        children: [],
      };
      byLabel.set(field.group, group);
      nodes.push(group);
    }
    if (!group.icon) group.icon = field.groupIcon ?? field.icon;
    if (field.groupSearchable) group.searchable = true;
    group.children.push(field);
  }
  return nodes;
}

/** PURE: case-insensitive substring test; a blank query matches everything. */
function matchesQuery(label: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return label.toLowerCase().includes(q);
}

/**
 * Below this many top-level rows the search box is dropped: a list this short is
 * read at a glance, so the input is one more thing to skip past (and a keyboard
 * trap on mobile) rather than a way to find anything. Small configs — the Office
 * Inbox's two fields, the admin two-field lists — render as a plain row list.
 */
const SEARCHABLE_MIN_NODES = 10;

// ── Rows ─────────────────────────────────────────────────────────────────────

/** A selectable field row — used at top level, in the flyout, and in the drill. */
const LeafRow = ({
  field,
  onAdd,
}: {
  field: FilterField;
  onAdd: (key: string) => void;
}) => (
  <CommandItem
    value={field.label}
    onSelect={() => onAdd(field.key)}
    className="group text-text-dark-high flex gap-2 items-center cursor-pointer"
  >
    {field.icon}
    <span className="text-sm leading-5 font-normal text-text-dark-max line-clamp-1 break-all">
      {field.label}
    </span>
  </CommandItem>
);

/** Mobile group row — taps drill into the group inside the same popover. */
const GroupDrillRow = ({
  group,
  onDrill,
}: {
  group: MenuGroup;
  onDrill: () => void;
}) => (
  <CommandItem
    value={group.label}
    onSelect={onDrill}
    className="group text-text-dark-high flex gap-2 items-center cursor-pointer"
  >
    {group.icon}
    <span className="text-sm leading-5 font-normal text-text-dark-max line-clamp-1 break-all grow">
      {group.label}
    </span>
    <BwChevronRightLine className="size-4 text-text-dark-high shrink-0" />
  </CommandItem>
);

/**
 * A group's member list, optionally with its own search box. Renders the members
 * in config order, filtered in React (`shouldFilter={false}`) so the order never
 * reshuffles as you type — cmdk's default scoring would re-sort by relevance and
 * then leave the DOM scrambled after the search clears. The search box is opt-in
 * per group (`group.searchable`, from a member's `groupSearchable`).
 */
const GroupMemberList = ({
  group,
  onAdd,
  listClassName,
}: {
  group: MenuGroup;
  onAdd: (key: string) => void;
  listClassName?: string;
}) => {
  const [search, setSearch] = useState("");
  const searchable = group.searchable;
  const visible = group.children.filter((child) =>
    matchesQuery(child.label, search),
  );
  return (
    <>
      {searchable && (
        <CommandInput
          value={search}
          onValueChange={setSearch}
          placeholder={`${group.label}...`}
        />
      )}
      <CommandList className={listClassName}>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup>
          {visible.map((child) => (
            <LeafRow key={child.key} field={child} onAdd={onAdd} />
          ))}
        </CommandGroup>
      </CommandList>
    </>
  );
};

/**
 * Desktop group row — hovering (or selecting via keyboard) opens a nested flyout
 * popover listing the group's members. A short close grace (120ms) keeps the
 * flyout open while the pointer travels the gap from the row to the panel.
 */
const DesktopGroupRow = ({
  group,
  side,
  onAdd,
}: {
  group: MenuGroup;
  side: "left" | "right";
  onAdd: (key: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<number | null>(null);

  const clearClose = () => {
    if (closeTimeout.current != null) {
      window.clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  };
  const scheduleClose = () => {
    clearClose();
    closeTimeout.current = window.setTimeout(() => setOpen(false), 120);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          onMouseEnter={() => {
            clearClose();
            setOpen(true);
          }}
          onMouseLeave={scheduleClose}
        >
          <CommandItem
            value={group.label}
            onSelect={() => setOpen(true)}
            className="group text-text-dark-high flex gap-2 items-center cursor-pointer"
          >
            {group.icon}
            <span className="text-sm leading-5 font-normal text-text-dark-max line-clamp-1 break-all grow">
              {group.label}
            </span>
            <BwChevronRightLine className="size-4 text-text-dark-high shrink-0" />
          </CommandItem>
        </div>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        sideOffset={8}
        className="p-0 min-w-50"
        onMouseEnter={clearClose}
        onMouseLeave={scheduleClose}
      >
        <AnimateChangeInHeight>
          <Command
            className="bg-bg-surface-elevate-low w-50"
            shouldFilter={false}
            tabIndex={0}
          >
            <GroupMemberList
              group={group}
              onAdd={onAdd}
              listClassName="outline-none"
            />
          </Command>
        </AnimateChangeInHeight>
      </PopoverContent>
    </Popover>
  );
};

/** Mobile drilled-in view — a back header + the group's members, in one popover. */
const DrillView = ({
  group,
  onBack,
  onAdd,
}: {
  group: MenuGroup;
  onBack: () => void;
  onAdd: (key: string) => void;
}) => (
  <Command className="bg-bg-surface-elevate-low w-50" shouldFilter={false}>
    <button
      type="button"
      onClick={onBack}
      className="text-text-dark-high border-border-dark-low flex gap-2 items-center w-full h-9 px-3 text-sm border-b"
    >
      <BwChevronLeftLine className="size-4 shrink-0" />
      <span className="font-medium">{group.label}</span>
    </button>
    <GroupMemberList
      group={group}
      onAdd={onAdd}
      listClassName="max-h-[70vh] outline-none"
    />
  </Command>
);

/**
 * Generic "+ Add filter" menu. Lists the not-yet-active fields; selecting one
 * adds an empty chip via `modify({ [key]: "" })` (the chip auto-opens to fill
 * the value). Replaces every per-feature `filter-type-command.tsx`. Rendered
 * both in the header (primary entry point) and inline in `<ActiveFilterBar>`.
 *
 * A top `<CommandInput>` filters the top-level rows by label — shown only once the
 * menu has `SEARCHABLE_MIN_NODES` rows to scan. Fields that share a
 * `group` collapse into one parent row that opens a submenu of its members — a
 * hover flyout on desktop, an in-place drill-down below `sm` (768px). Flat
 * configs (no `group`) render as a plain searchable list, unchanged.
 *
 * Filtering is done in React with `shouldFilter={false}`, NOT cmdk's built-in
 * scoring: cmdk re-sorts rows by match score as you type and leaves the DOM in
 * that scrambled order after the search clears — so config order would not be
 * stable. Rendering the matched subset ourselves keeps the order fixed.
 */
export const FilterMenu = ({
  fields,
  align = "end",
  size = "icon-md",
  className,
}: FilterMenuProps) => {
  const [open, setOpen] = useState(false);
  const [drill, setDrill] = useState<MenuGroup | null>(null);
  const [search, setSearch] = useState("");
  // Below `sm` (768px) there's no room for a side flyout — drill in place instead.
  const inlineSubmenus = useScreen({ base: true, sm: false });
  const { filters, filtersOrder, modify } = useFilters();

  // Reset any drill state when switching to the desktop flyout (e.g. on resize).
  useEffect(() => {
    if (!inlineSubmenus) setDrill(null);
  }, [inlineSubmenus]);

  const activeKeys = Object.keys(filters ?? {}).filter(
    (key) => (filters as Record<string, unknown>)[key] !== undefined,
  );
  const available = fields.filter((field) => !activeKeys.includes(field.key));

  if (isEmpty(available)) return null;

  const nodes = buildMenuNodes(available);
  const searchable = nodes.length >= SEARCHABLE_MIN_NODES;
  const visibleNodes = nodes.filter((node) =>
    node.type === "leaf"
      ? matchesQuery(node.field.label, search)
      : matchesQuery(node.label, search),
  );

  const onAdd = (key: string) => {
    modify({ [key]: "" });
    setDrill(null);
    setSearch("");
    setOpen(false);
  };

  // Size the icon with a Tailwind class, not the numeric `size` prop: the
  // Button base applies `[&_svg:not([class*='size-'])]:size-5`, and CSS
  // width/height beats the svg's width/height attributes — so a numeric size is
  // ignored. A `size-*` class opts the svg out of that selector. Mirrors
  // office's filter-toggle CVA (icon-md → 20px, icon-xs → 16px).
  const iconSizeClass = size === "icon-md" ? "size-5" : "size-4";

  // Flyout opens toward open space: right when the menu sits on the left (the
  // inline bar, filters already active), left when it sits on the right (header).
  const flyoutSide = filtersOrder.length > 0 ? "right" : "left";

  return (
    <Popover
      modal={true}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setDrill(null);
          setSearch("");
        }
      }}
    >
      {/* An icon-only button: the label is its only name, for screen readers and for tests. */}
      <PopoverTrigger asChild>
        <Button
          kind="ghost"
          variant="neutral"
          size={size}
          aria-label="Add filter"
          className={className}
        >
          <BwFilterLine className={iconSizeClass} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="p-0 w-auto">
        <AnimateChangeInHeight>
          {inlineSubmenus && drill ? (
            <DrillView
              key={drill.label}
              group={drill}
              onBack={() => {
                setDrill(null);
                setSearch("");
              }}
              onAdd={onAdd}
            />
          ) : (
            <Command
              className="bg-bg-surface-elevate-low w-50"
              shouldFilter={false}
              tabIndex={0}
            >
              {searchable && (
                <CommandInput
                  value={search}
                  onValueChange={setSearch}
                  placeholder="Filter..."
                />
              )}
              <CommandList className="max-h-[70vh] outline-none">
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  {visibleNodes.map((node) =>
                    node.type === "leaf" ? (
                      <LeafRow
                        key={node.field.key}
                        field={node.field}
                        onAdd={onAdd}
                      />
                    ) : inlineSubmenus ? (
                      <GroupDrillRow
                        key={node.label}
                        group={node}
                        onDrill={() => {
                          setDrill(node);
                          setSearch("");
                        }}
                      />
                    ) : (
                      <DesktopGroupRow
                        key={node.label}
                        group={node}
                        side={flyoutSide}
                        onAdd={onAdd}
                      />
                    ),
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          )}
        </AnimateChangeInHeight>
      </PopoverContent>
    </Popover>
  );
};
